import Vue from "vue";

// <iconify-icon> is a custom element registered by the Iconify script in
// index.html, not a Vue component — tell Vue to leave it alone.
Vue.config.ignoredElements = [/^iconify-icon$/];
import App from "./App.vue";
import router from "./app-js/router.js";
import store from "./store";
import vuetify from "./plugins/vuetify";
import installEscapeToClose from "./plugins/escape-to-close";
import stickyXScroll from "./behaviours/stickyXScroll";
import stickyHead from "./behaviours/stickyHead";
import scrollQuiet from "./behaviours/scrollQuiet";
import axios from "axios";
import rules from "./app-js/rules";
import messages from "./app-js/messages";
import wysiwyg from "vue-wysiwyg";
import _ from "lodash";

import axios_req from "./app-js/new-base-axios";
import urls from "./app-js/api-urls";
import accessPlugin from "./plugins/access";
import sessionPlugin from "./app-js/session";

// Clipboard fallback: navigator.clipboard is only available in secure contexts
// (https / localhost). The admin is accessed over plain http on an IP, where
// navigator.clipboard is undefined, so "Copy link" buttons (KYC / bank statement
// / payment links) silently fail. Install an execCommand-based writeText so the
// real URL is actually copied. Runs before any component mounts.
if (
  typeof navigator !== "undefined" &&
  (!navigator.clipboard || !window.isSecureContext)
) {
  const legacyWriteText = (text) =>
    new Promise((resolve, reject) => {
      try {
        const ta = document.createElement("textarea");
        ta.value = text == null ? "" : String(text);
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        ok ? resolve() : reject(new Error("copy command failed"));
      } catch (e) {
        reject(e);
      }
    });
  try {
    Object.defineProperty(navigator, "clipboard", {
      configurable: true,
      value: { writeText: legacyWriteText },
    });
  } catch (e) {
    /* navigator.clipboard is read-only in this browser; leave it untouched */
  }
}

// Eventbus globally declare
export const eventBus = new Vue();

const eventBus1 = new Vue();
Vue.prototype.eventBus1 = eventBus1;
Vue.config.productionTip = false;
// Vue.prototype.$urls = urls;
Vue.prototype.$rules = rules;
Vue.prototype.$messages = messages;
// `this` is the calling component. The wrapper uses it to tell a repeated
// click (same component, same write, still in flight) from the same request
// sent by another screen; see inFlightWrites in app-js/new-base-axios.js.
Vue.prototype.$Axios = function (method, url, options = {}) {
  return axios_req(method, url, { ...options, owner: this });
};
Vue.prototype.$apiUrl = urls;
Vue.prototype.$_ = _;
// export const EventBus = new Vue();
// axios.defaults.xsrfCookieName = "csrftoken";
// axios.defaults.xsrfHeaderName = "X-CSRFTOKEN";

axios.defaults.baseURL = process.env.VUE_APP_BASE_URL;

// LIcon (the inlined Lucide set) is now carried by every create-lead step
// footer as well as the list screens — ~30 components. Registering it once here
// beats repeating an async `components: { LIcon: () => import(...) }` block in
// each of them, and the per-file registrations that already exist still work.
Vue.component("LIcon", () => import("@/components/common/LIcon.vue"));

Vue.use(wysiwyg, {}); // config is optional. more below
Vue.use(accessPlugin);
// The shared login's idle clock, cross-tab logout and `$noteAction`.
Vue.use(sessionPlugin);
Vue.component("PermissionGate", () => import("@/components/common/PermissionGate.vue"));

// Tab permissions are checked for every navigation by `router.beforeEach` in
// app-js/router.js (the old per-component beforeRouteUpdate only fired on
// in-place param changes, so typing a tab's URL was never checked).
Vue.mixin({
  methods: {
    showSnackBar(color, time, text) {
      this.$store.dispatch("snackBar/Snackbar", {
        show: true,
        color: color,
        message: text,
        timeout: time,
      });
    },

    // Returns true when the given URL points to a PDF (tolerates query strings).
    isPdf(url) {
      if (!url) return false;
      return url.split("?")[0].split(".").pop().toLowerCase() === "pdf";
    },

    // Returns true when the given URL points to an image (tolerates query strings).
    isImageDocument(url) {
      if (!url) return false;
      const ext = url.split("?")[0].split(".").pop().toLowerCase();
      return ["png", "jpg", "jpeg", "webp", "gif", "bmp"].includes(ext);
    },

    // True when the URL is served by neither the SPA nor our own API, i.e. it is
    // a verification provider's host (Signzy / Finvu). Those send no CORS header,
    // so pdf.js cannot read them directly and have to go through the proxy.
    isExternalDocument(url) {
      if (!url) return false;
      try {
        const parsed = new URL(url, window.location.origin);
        // blob: and data: URLs are already local to the page and have no host;
        // sending one to the proxy would just 403.
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
          return false;
        }
        if (parsed.host === window.location.host) return false;
        return parsed.host !== new URL(process.env.VUE_APP_BASE_URL).host;
      } catch (e) {
        // Not a URL we can reason about; leave it to the caller's fallback.
        return false;
      }
    },

    // Streams a provider-hosted document through our own backend, which sets
    // Access-Control-Allow-Origin so pdf.js can read it, as a real PDF content
    // type, with Content-Disposition: inline. Note this URL is on the API host,
    // not the SPA's origin -- it works because of that header, not because it
    // is same-origin.
    proxiedDocumentUrl(url) {
      return `${process.env.VUE_APP_BASE_URL}${
        urls.DOCUMENT_PROXY
      }?url=${encodeURIComponent(url)}`;
    },

    // Single entry point for "view this document" buttons. Prefer this over
    // hand-rolling an isImageDocument/isPdf/window.open chain at the call site.
    openDocument(url, name) {
      if (!url) return;
      if (this.isImageDocument(url)) return this.previewImage(url, name);
      // Every external document goes through the proxy, including the ones
      // ending in `.pdf`: provider hosts (Cashpo via CloudFront, Signzy) send no
      // CORS header, and pdf.js reads the bytes over XHR rather than handing the
      // URL to an iframe, so it cannot load them directly. Extension-less URLs
      // are provider-hosted KYC documents, which are PDFs in practice; if one
      // turns out not to be, the viewer's error state still offers
      // "Open in new tab".
      // This has to come before the isPdf check, which would otherwise
      // short-circuit provider PDFs into an unproxied load that always fails.
      if (this.isExternalDocument(url)) {
        return this.openPdfDialog(this.proxiedDocumentUrl(url), name, url);
      }
      // Our own media is served by nginx with Access-Control-Allow-Origin set,
      // so load it direct: that keeps range requests (progressive rendering) and
      // avoids putting document transfers back on a gunicorn worker.
      if (this.isPdf(url)) return this.openPdfDialog(url, name);
      window.open(url, "_blank");
    },

    // Opens the global in-app PDF viewer dialog (mounted once in App.vue).
    // `sourceUrl` is the original document URL when `url` is a proxied one, so
    // the dialog's "Open in new tab" link points at the real document.
    openPdfDialog(url, name, sourceUrl) {
      this.$store.dispatch("pdfViewer/open", {
        url,
        name: name || "",
        sourceUrl: sourceUrl || url,
      });
    },

    // Opens the standalone image viewer in a new tab.
    // The image URL goes in the query string, never the path: a path segment
    // ending in `.jpg`/`.png` is treated as a missing static asset by the web
    // server and 404s instead of falling through to index.html.
    previewImage(url, document_name) {
      const route = this.$router.resolve({
        name: "image-viewer",
        query: { image: url, document: document_name || "" },
      });
      window.open(route.href, "_blank");
    },

    // Ask the backend whether a bank account number is already used by another
    // customer (or by this customer before). Matching is on the account number
    // string only (leading zeros preserved). Resolves the result object
    // { requires_confirmation, shared_with_other_customer,
    //   seen_for_this_customer, other_customers, same_customer,
    //   already_acknowledged }.
    // Gate on `requires_confirmation` ONLY — it is true just for a DIFFERENT
    // customer on the same account. The same customer reusing their own account
    // on a second loan is normal and needs no confirmation; it shows up in
    // `same_customer` / `seen_for_this_customer` for information only.
    // On any network/error, resolves an empty object so a save is never blocked
    // by the check itself.
    checkAccountShared(accountNumber, leadCreation) {
      return new Promise((resolve) => {
        this.$Axios("post", this.$apiUrl.CHECK_ACCOUNT_SHARED, {
          data: {
            account_number: accountNumber,
            lead_creation: leadCreation || "",
          },
          onSuccess: (res) => resolve((res && res.data && res.data.result) || {}),
          onFailure: () => resolve({}),
          isTokenRequired: true,
          showErrorMessage: false,
          // A read: an automatic and a clicked gate run may overlap.
          allowDuplicate: true,
        });
      });
    },

    // Flatten a checkAccountShared() result into one list of matched users
    // (other customers first, then same-customer earlier records).
    accountMatchList(result) {
      if (!result) return [];
      return [
        ...(result.other_customers || []),
        ...(result.same_customer || []),
      ];
    },

    // Format one matched user consistently as "Name (lead id) — mobile".
    accountUserLabel(user) {
      if (!user) return "";
      let label = user.name || "Customer";
      if (user.lead_creation) label += ` (${user.lead_creation})`;
      if (user.mobile) label += ` — ${user.mobile}`;
      return label;
    },

    // Opens the global account-conflict confirm popup (mounted once in App.vue).
    // Resolves true if the user taps "Yes, continue", false otherwise.
    confirmAccountConflict(result) {
      return this.$store.dispatch("accountConflict/confirm", {
        other_customers: (result && result.other_customers) || [],
        same_customer: (result && result.same_customer) || [],
      });
    },

    // Persist (backend) that staff accepted the shared-account warning for this
    // loan/account, so the warning is never shown again on future visits.
    acknowledgeSharedAccount(accountNumber, leadCreation) {
      if (!accountNumber || !leadCreation) return Promise.resolve(false);
      return new Promise((resolve) => {
        this.$Axios("post", this.$apiUrl.ACKNOWLEDGE_SHARED_ACCOUNT, {
          data: { account_number: accountNumber, lead_creation: leadCreation },
          onSuccess: () => resolve(true),
          onFailure: () => resolve(false),
          isTokenRequired: true,
          showErrorMessage: false,
          // Setting the same flag twice is harmless; see checkAccountShared.
          allowDuplicate: true,
        });
      });
    },

    formate_date(value) {
      let date = value;
      const [year, month, day] = date.split("-");
      return `${day.padStart(2, "0")}-${month.padStart(2, "0")}-${year}`;
    },

    /**
     * Change LOS loan status.
     * @param {Object} loan_status - Next loan status.
     */
    updateLosStep(loan_status) {
      // console.log("loan_status",loan_status);
      let formdata = {
        loan_status: loan_status,
      };
      let look_up_key = this.$route.params.id;

      const successHandler = () => {
        this.$store.dispatch("losDetails/setCount");
      };
      const failureHandler = () => {
        return false;
      };

      return this.$Axios("put", this.$apiUrl.UPDATE_LOS_LOAN_STATUS, {
        data: formdata,
        lookUpKey: look_up_key,
        onSuccess: successHandler,
        onFailure: failureHandler,
        isTokenRequired: true,
      });
    },

    syncLeadCreationToRoute(leadCreation) {
      if (this.$route && ["add-lead-list", "agent-create-lead"].includes(this.$route.name)) {
        this.$router.replace({
          name: this.$route.name,
          params: { ...this.$route.params, lead_creation: leadCreation || undefined },
        });
      }
    },

    copyLink(value) {
      const text = value;

      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(text)
          .then(() => {
            alert("Link copied to clipboard");
          })
          .catch((err) => {
            console.error("Failed to copy link:", err);
          });
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.top = 0;
        textArea.style.left = 0;

        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand("copy");
          console.log(
            "Fallback: Copy was " + (successful ? "successful" : "unsuccessful")
          );
        } catch (err) {
          console.error("Fallback: Unable to copy", err);
        }

        document.body.removeChild(textArea);
      }
    },

    formateDateDmy(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      let new_date = `${day}-${month}-${year}`;
      return new_date;
    },
    formateDateYmd(date) {
      if (!date) return null;
      const [day, month, year] = date.split("-");
      let new_date = `${year}-${month}-${day}`;
      return new_date;
    },
  },
});

// Escape closes the topmost dialog, persistent ones included. Must run before
// the first dialog is created — see plugins/escape-to-close.js.
installEscapeToClose();

// Pins each list screen's sideways scrollbar within reach of the pager instead
// of leaving it at the bottom of the table, below the fold. Finds its own
// targets, so a list screen added later is covered without editing a list here.
// See behaviours/stickyXScroll.js.
Vue.use(stickyXScroll, { router });
// See behaviours/stickyHead.js.
Vue.use(stickyHead, { router });
// Rows ignore the mouse while the page scrolls under it. See
// behaviours/scrollQuiet.js.
Vue.use(scrollQuiet);

new Vue({
  router,
  store,
  vuetify,

  render: (h) => h(App),
}).$mount("#app");
